<script setup lang="ts">
import type { AnimationItem } from 'lottie-web'
import type { NuxtError } from '#app'

const route = useRoute()

const _props = defineProps<{ error: NuxtError }>()

// if (import.meta.dev) {
//   console.log('route', route)
//   console.log(_props.error)
// }

const { data } = useSanityQuery<SettingsProps>(getSettings)

const handleError = () => clearError({ redirect: '/' })

watch(
  () => route.fullPath,
  () => {
    clearError()
  },
)

useHead({
  title: '404',
  htmlAttrs: { lang: 'en' },
  meta: [
    { property: 'og:title', content: '404' },
    { name: 'description', content: 'Error page' },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0',
    },
  ],
})
document?.body?.classList.add(...['bg-black'])
const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

// ------- LOTTIE ANIMATION -------
const $animation = useTemplateRef('$animation')
const lottieInstance = ref<AnimationItem | null>(null)
async function lottieAnimation() {
  return new Promise(async (resolve, _reject) => {
    if ($animation.value) {
      const Lottie = (await import('lottie-web')).default
      $animation.value.style.transition = 'opacity 200ms'
      $animation.value.style.opacity = '0'

      lottieInstance.value = Lottie.loadAnimation({
        loop: false,
        container: $animation.value,
        path: '/lottie/love-from-nitex.json',
        autoplay: false,
      })

      lottieInstance.value.addEventListener('DOMLoaded', () => {
        if ($animation.value) {
          $animation.value.style.opacity = '1'
        }

        lottieInstance.value?.setSpeed(1.3)

        setTimeout(() => {
          lottieInstance.value?.play()
        }, 500)
      })
      lottieInstance.value.addEventListener('complete', resolve)
    }
  })
}

// ------- GSAP ANIMATION -------
const container = ref<HTMLElement | null>(null)
const target = ref<HTMLElement | null>(null)
let hasLottieRun = false

useGsapAnimation('404-reveal', (gsap) => {
  const tl = gsap.timeline({ paused: true })
  if (!target.value || !container.value) return
  const icon4Left = container.value?.querySelectorAll('.icon-4-left')
  const icon4Right = container.value?.querySelectorAll('.icon-4-right')
  tl.to(icon4Left, {
    transform: 'translate3d(0%, 0px, 0px)',
    ease: 'power2.inOut',
    duration: 1,
  })
    .to(
      icon4Right,
      {
        transform: 'translate3d(0%, 0px, 0px)',
        ease: 'power2.inOut',
        duration: 1,
      },
      '<',
    )
    .to(
      target.value,
      {
        opacity: 1,
        scale: isMobile.value ? 0.5 : 0.8,
        clipPath: 'polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)',
        ease: 'power2.inOut',
        duration: 1,
        onStart: () => {
          if (hasLottieRun) return
          lottieAnimation()
          hasLottieRun = true
        },
      },
      '<',
    )
  tl?.play()
  return tl
})
</script>

<template>
  <div
    v-if="error"
    class="text-neon-yellow flex h-dvh w-full justify-center bg-black py-20 font-mono"
  >
    <SiteHeader />

    <div
      ref="container"
      class="center px-bleed flex h-full max-h-full shrink-1 flex-col items-center justify-center gap-10 lg:gap-12"
    >
      <div class="relative flex h-full flex-row items-center justify-between overflow-hidden">
        <Icons4
          class="icon-4-left default-anim-state-icon-left absolute left-0 w-fit max-w-1/2 lg:right-0 lg:left-[unset]"
        />
        <div ref="target" class="media-wrapper default-anim-state-target relative h-fit max-h-full">
          <div
            class="text-neon-yellow pointer-events-none absolute inset-0 z-1 grid place-items-center"
          >
            <div ref="$animation" class="w-1/2 max-w-xl lg:w-[85vw]" />
          </div>
          <CommonMedia :media="data?.errorPage?.media" :aspect="1.5" class="max-h-full" />
        </div>
        <Icons4
          class="icon-4-right default-anim-state-icon-right absolute right-0 w-fit max-w-1/2 lg:right-[unset] lg:left-0"
        />
      </div>
      <div
        class="content px-bleed flex max-w-xl flex-col items-center justify-center gap-8 text-center"
      >
        <p class="text-base">{{ data?.errorPage?.text }}</p>
        <button
          class="before:bg-neon-yellow relative inline-block w-fit px-3 py-2 font-mono text-sm text-black uppercase transition-all before:absolute before:inset-0 before:z-0 before:transition-transform before:duration-300 before:ease-out hover:tracking-wider hover:before:scale-x-[1.1] hover:before:-skew-x-[20deg]"
          @click="handleError"
        >
          <span class="relative z-[1]"> go to homepage </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.default-anim-state-target {
  scale: 0.2;
  opacity: 0.2;
  clip-path: polygon(70% 0, 80% 0, 35% 100%, 25% 100%);
}
.default-anim-state-icon-left {
  /* right: 100%; */
  transform: translate3d(-100%, 0px, 0px);
}
.default-anim-state-icon-right {
  transform: translate3d(100%, 0px, 0px);
}

.navigation-header.page {
  --logo: var(--color-neon-yellow);
}
</style>
